/**
 * Landing Page — Infirmly Swiss dark navy design (matches official mockup).
 * Sections: Header • Hero Carousel • Categories Grid • Trust Badges • How It Works • Pricing • CTA • Footer
 */
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, Building } from "lucide-react";
import { HeroCarousel } from "@/components/HeroCarousel";
import { SuperCategories } from "@/components/SuperCategories";
import { TrustBadges, HowItWorks } from "@/components/HomeSections";

const Logo = ({ className = "h-9", priority = false, decorative = false }) => (
  <img
    src="https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png"
    alt={decorative ? "" : "Infirmly Swiss Consulting logo"}
    aria-hidden={decorative ? "true" : undefined}
    className={`${className} rounded-full bg-white ring-2 ring-white/25 shadow-lg shadow-blue-900/40 object-cover`}
    width="48"
    height="48"
    loading={priority ? "eager" : "lazy"}
    fetchpriority={priority ? "high" : "auto"}
    decoding="async"
  />
);

const LandingPage = ({ t, language, LanguageSelector }) => {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen text-white relative"
      style={{
        background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 50%, #050B23 100%)",
      }}
      data-testid="landing-page"
    >
      {/* Scenic backdrop (subtle, decorative — hidden from AT) */}
      <div
        aria-hidden="true"
        role="presentation"
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?auto=format&fit=crop&w=2000&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "overlay",
        }}
      />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(5,11,35,0) 0%, #050B23 90%)" }} />

      <div className="relative">
        {/* Top action bar — Language selector + Login, always in its own row above the brand */}
        <div className="relative z-10 flex items-center justify-end gap-2 px-4 sm:px-6 pt-4 pb-2" data-testid="landing-top-bar">
          {LanguageSelector && <LanguageSelector />}
          <Button
            onClick={() => navigate("/login")}
            variant="ghost"
            className="text-white hover:bg-white/10 focus-visible:bg-white/10 rounded-full text-sm min-h-[40px] px-4"
            data-testid="login-btn"
            aria-label={t ? t("login") || "Login" : "Login"}
          >
            {t ? t("login") || "Login" : "Login"}
          </Button>
        </div>

        {/* Brand strip */}
        <header className="pt-2 pb-4 text-center px-4">
          <div className="flex justify-center items-center gap-3">
            <Logo className="h-12 w-12" priority />
            <div className="text-left">
              <p className="text-white font-extrabold text-xl tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                INFIRMLY
              </p>
              <p className="text-hi-contrast-sub text-xs font-medium tracking-widest -mt-0.5">CONSULTING</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-hi-contrast-sub text-[11px] font-semibold tracking-[0.2em]">
            <span aria-hidden="true" className="h-px w-8 sm:w-12 bg-blue-200/30" />
            <span>SWISS CONSULTING</span>
            <span aria-label="Swiss flag" role="img" className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-600 text-white">
              <svg aria-hidden="true" viewBox="0 0 16 16" className="w-3 h-3 fill-white"><rect x="6" y="2" width="4" height="12"/><rect x="2" y="6" width="12" height="4"/></svg>
            </span>
            <span>PLATFORM</span>
            <span aria-hidden="true" className="h-px w-8 sm:w-12 bg-blue-200/30" />
          </div>
        </header>

        {/* Hero text */}
        <section className="px-4 sm:px-6 max-w-3xl mx-auto text-center pt-4 pb-8">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-balance" style={{ fontFamily: "Chivo, sans-serif" }}>
            {t ? t("findTrustedHeader") || "Find Trusted Swiss Experts" : "Find Trusted Swiss Experts"}
          </h1>
          <p className="text-hi-contrast-sub mt-4 text-base sm:text-lg max-w-2xl mx-auto">
            {t ? t("findTrustedSub") || "Submit your request, connect directly, and book appointments — all in one secure platform." : "Submit your request, connect directly, and book appointments — all in one secure platform."}
          </p>
        </section>

        {/* App card preview */}
        <section className="px-4 sm:px-6 max-w-3xl mx-auto pb-12">
          <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-3xl p-4 sm:p-6 shadow-2xl space-y-5">
            <HeroCarousel language={language} />

            <SuperCategories language={language} onSelect={() => navigate("/register")} />

            <TrustBadges language={language} />

            <HowItWorks language={language} />
          </div>
        </section>

        {/* Pricing */}
        <section className="px-4 sm:px-6 max-w-5xl mx-auto pb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t ? t("pricingTitle") || "Simple, fair pricing" : "Simple, fair pricing"}
            </h2>
            <p className="text-blue-100/85 mt-2">{t ? t("pricingDesc") : "Pay once a year — full Swiss access."}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Client */}
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
              <p className="text-blue-200/70 text-sm font-medium">{t ? t("clientPlanTitle") : "For Clients"}</p>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-5xl font-black text-white" style={{ fontFamily: "Chivo, sans-serif" }}>CHF 19</span>
                <span className="text-blue-100/60 text-sm">/ {t ? t("perYear") || "year" : "year"}</span>
              </div>
              <p className="text-blue-100/85 text-xs mt-1">≈ CHF 1.58 / {t ? t("month") || "month" : "month"}</p>
              <ul className="space-y-2 mt-5 mb-6 text-sm">
                {[
                  { de: "Unbegrenzte Anfragen", en: "Unlimited requests", fr: "Demandes illimitées", it: "Richieste illimitate" },
                  { de: "Direkter Chat mit Experten", en: "Direct chat with experts", fr: "Discussion directe avec les experts", it: "Chat diretta con esperti" },
                  { de: "Termin-Buchung", en: "Appointment booking", fr: "Prise de rendez-vous", it: "Prenotazione appuntamenti" },
                  { de: "Nur verifizierte Profis", en: "Verified professionals only", fr: "Professionnels vérifiés uniquement", it: "Solo professionisti verificati" },
                ].map((f, i) => (
                  <li key={i} className="flex gap-2 text-blue-100/80">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    {f[language] || f.en}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate("/register?role=client")} className="w-full rounded-full bg-white text-[#0A1A3E] hover:bg-blue-50 font-semibold h-11" data-testid="client-plan-btn">
                {t ? t("startNow") || "Get started" : "Get started"}
              </Button>
            </div>

            {/* Expert */}
            <div className="bg-gradient-to-br from-[#0044CC] to-[#003399] border border-[#60a5fa]/30 rounded-2xl p-7 relative shadow-2xl">
              <Badge className="absolute top-5 right-5 bg-white text-[#0044CC] font-bold">PRO</Badge>
              <p className="text-blue-100 text-sm font-medium">{t ? t("expertPlanTitle") : "For Experts"}</p>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-5xl font-black text-white" style={{ fontFamily: "Chivo, sans-serif" }}>CHF 179</span>
                <span className="text-blue-200 text-sm">/ {t ? t("perYear") || "year" : "year"}</span>
              </div>
              <p className="text-blue-100/90 text-xs mt-1">≈ CHF 14.90 / {t ? t("month") || "month" : "month"}</p>
              <ul className="space-y-2 mt-5 mb-6 text-sm">
                {[
                  { de: "Kundenanfragen empfangen", en: "Receive client requests", fr: "Recevoir des demandes clients", it: "Ricevi richieste clienti" },
                  { de: "Verifiziertes Abzeichen", en: "Verified badge", fr: "Badge vérifié", it: "Badge verificato" },
                  { de: "Terminverwaltung", en: "Appointment management", fr: "Gestion des rendez-vous", it: "Gestione appuntamenti" },
                  { de: "Bevorzugte Platzierung", en: "Priority listing", fr: "Référencement prioritaire", it: "Inserzione prioritaria" },
                ].map((f, i) => (
                  <li key={i} className="flex gap-2 text-blue-100">
                    <Check className="w-4 h-4 text-green-300 flex-shrink-0 mt-0.5" />
                    {f[language] || f.en}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate("/register?role=provider")} className="w-full rounded-full bg-white text-[#0044CC] hover:bg-blue-50 font-semibold h-11" data-testid="expert-plan-btn">
                {t ? t("saveNow") || "Save now" : "Save now"}
              </Button>
            </div>
          </div>
        </section>

        {/* Install App */}
        <section className="px-4 sm:px-6 max-w-5xl mx-auto pb-16">
          <Card className="bg-white/[0.04] border border-white/10 backdrop-blur-sm rounded-2xl p-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                {t ? t("installApp") || "Install the app" : "Install the app"}
              </h3>
              <p className="text-blue-100/70 mb-6 text-sm">
                {t ? t("installDesc") || "Scan to install on your phone — fully native experience." : "Scan to install on your phone — fully native experience."}
              </p>

              {/* Store badges */}
              <div className="flex flex-wrap gap-3 mb-5" data-testid="store-badges">
                <a
                  href="https://apps.apple.com/ch/app/infirmly-swiss/id6760337104?l=en-GB"
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black hover:bg-zinc-900 border border-white/10 rounded-xl px-4 py-2.5 transition-colors"
                  data-testid="appstore-badge"
                >
                  <svg className="w-7 h-7" viewBox="0 0 384 512" fill="white" aria-hidden="true" focusable="false">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="text-blue-100/70 text-[10px]">Download on the</p>
                    <p className="text-white font-semibold text-sm">App Store</p>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=ch.infirmly.app"
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black hover:bg-zinc-900 border border-white/10 rounded-xl px-4 py-2.5 transition-colors"
                  data-testid="playstore-badge"
                >
                  <svg className="w-7 h-7" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
                    <path fill="#00d2ff" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z"/>
                    <path fill="#ffcb2b" d="M104.6 499L325.3 277.7l60.1 60.1L104.6 499z"/>
                    <path fill="#ff4d59" d="M385.4 337.8l-60.1-60.1 60.1-60.1 76.7 44.1c20 11.4 20 33.6 0 45L385.4 337.8z"/>
                    <path fill="#36c963" d="M104.6 13L325.3 234.3l-60.1 60.1L104.6 499V13z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="text-blue-100/70 text-[10px]">GET IT ON</p>
                    <p className="text-white font-semibold text-sm">Google Play</p>
                  </div>
                </a>
              </div>

              <ul className="space-y-2 text-blue-100/80 text-sm">
                <li className="flex gap-2"><Bell className="w-4 h-4 text-[#60a5fa] flex-shrink-0 mt-0.5" />{t ? t("getNotifications") || "Push notifications" : "Push notifications"}</li>
              </ul>
            </div>
            <div className="text-center">
              <div className="inline-block bg-white p-3 rounded-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_777d69f7-8a71-4799-b1ef-ba31f00a69a5/artifacts/u49je9d3_qr-code%20%285%29.png"
                  alt="QR code — scan with your phone camera to open Infirmly Swiss on your device"
                  width={180} height={180}
                  className="block"
                  loading="lazy"
                  decoding="async"
                  data-testid="qr-code"
                />
              </div>
              <p className="text-hi-contrast-sub text-xs mt-3">infirmlyapp.gkv-consulting.com</p>
            </div>
          </Card>
        </section>

        {/* GKV strip */}
        <div className="bg-white/[0.02] border-t border-white/10 py-4 px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-center gap-3 text-center md:text-left">
            <Building className="w-5 h-5 text-blue-200/70" />
            <span className="text-sm text-blue-100/70">
              {t ? t("productOf") || "A product of" : "A product of"}{" "}
              <a href="https://gkv-consulting.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-white hover:text-[#60a5fa]">
                GKV-Consulting GmbH
              </a>
            </span>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8 px-4 sm:px-6 border-t border-white/10">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2"><Logo className="h-7 w-7" decorative /><span className="text-white font-bold">Infirmly Swiss</span></div>
            <div className="flex gap-5 text-sm text-blue-100/60">
              <Link to="/impressum" className="hover:text-white transition-colors">{t ? t("impressum") : "Imprint"}</Link>
              <Link to="/agb" className="hover:text-white transition-colors">{t ? t("agb") : "Terms"}</Link>
              <Link to="/datenschutz" className="hover:text-white transition-colors">{t ? t("datenschutz") : "Privacy"}</Link>
            </div>
            <p className="text-sm text-blue-100/50">© 2026 GKV-Consulting GmbH, Zürich</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
